import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Text, TextType } from 'ui-components/components/text';
import { DimensionInput } from 'ui-components/components/dimension-input';
import { WeightInput } from 'ui-components/components/weight-input';
import { Input } from 'ui-components/components/input';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { SimpleModal } from '../../../../../components/simple-modal';
import { BannerMessageV2 } from 'ui-components/components/banner-message/banner-message-v2';
import { BannerStatus } from 'ui-components/components/banner-message/banner-enums';
import { FlexColumn, Gap } from 'ui-components/components/flex';

import type { Weight } from '@shipstation/client-common/types/weight';
import { convertWeight } from '../../../../../services/unit-services/weights/weight-convert-service';
import { messages, formMessages } from './messages';
import { useTrackWorkflow, WorkflowSteps } from '../../../../../tracking/use-workflow-tracking';
import { Workflows } from '../../../../../tracking/tracking-constants';
import Global from '../../../../../store/global-selectors';
import { useEnableCustomPackageWeights } from '../../../../../feature-flags/hooks/use-enable-custom-package-weights';
import { useIsSellerFeatureEnabled } from '../../../../../hooks/use-is-seller-feature-enabled';
import { PackingRecommendations } from '../../../../../constants/features';
import type { Dimensions } from '../../../../../prop-types/dimension-types.d';
import type { DimensionUnit } from '../../../../../prop-types/dimenstion-unit.d';
import type { MODALS } from '../../constants';
import style from './custom-packages-modal.module.less';

type PackageInfo = {
  dimensions?: Dimensions;
  name?: string;
  tareWeight?: Weight | null;
  maxWeight?: Weight | null;
};

type NormalizedPackageInfo = {
  name?: string;
  dimensions?: Dimensions;
  tareWeight: number | null;
  maxWeight: number | null;
};
type CustomPackagesModalProps = {
  action: MODALS.Add | MODALS.Edit;
  onClose: () => void;
  onSave: (
    formValues: NormalizedPackageInfo,
    setErrorMessage: (value: string | null) => void,
    setIsSaving: (value: boolean) => void,
  ) => void;
  packageInfo?: PackageInfo;
  unit: string;
};

const defaultPackageInfo: PackageInfo = {
  dimensions: {
    length: 0,
    width: 0,
    height: 0,
    unit: '' as DimensionUnit,
  },
  tareWeight: null,
  maxWeight: null,
  name: '',
};

export const CustomPackagesModal = ({ action, packageInfo = defaultPackageInfo, onClose, onSave, unit }: CustomPackagesModalProps) => {
  const isWeightsEnabled = useEnableCustomPackageWeights();
  const isPackingRecommendationsEnabled = useIsSellerFeatureEnabled(PackingRecommendations);
  const shouldShowMaxWeight = isWeightsEnabled || isPackingRecommendationsEnabled;
  const sellerWeightUnit = useSelector(Global.selectSellerDefaultWeightUnit);
  const weightInputUnit = sellerWeightUnit?.toLowerCase();

  const normalizeWeight = (weightObj: Weight | null | undefined): number | null => {
    if (!weightObj || weightObj.value === null || weightObj.value === undefined) {
      return null;
    }

    const sourceUnit = weightObj.unit || sellerWeightUnit;
    const { value: convertedValue } = convertWeight({ value: weightObj.value, unit: sourceUnit }, sellerWeightUnit);

    return Math.round(convertedValue * 1000) / 1000;
  };

  const normalizeDimensions = (dims: Dimensions | undefined): Dimensions | undefined => {
    if (!dims) return dims;
    return {
      ...dims,
      length: dims.length ? parseFloat(dims.length.toFixed(2)) : dims.length,
      width: dims.width ? parseFloat(dims.width.toFixed(2)) : dims.width,
      height: dims.height ? parseFloat(dims.height.toFixed(2)) : dims.height,
      unit: (dims.unit || unit) as DimensionUnit,
    };
  };

  const normalizedPackageInfo: NormalizedPackageInfo = {
    ...packageInfo,
    tareWeight: normalizeWeight(packageInfo.tareWeight),
    maxWeight: normalizeWeight(packageInfo.maxWeight),
    dimensions: normalizeDimensions(packageInfo.dimensions),
  };

  const [formValues, setFormValues] = useAsyncSafeState(normalizedPackageInfo);
  const [isSaving, setIsSaving] = useAsyncSafeState(false);
  const [errorMessage, setErrorMessage] = useAsyncSafeState(null);
  const trackCustomPackageFlow = useTrackWorkflow(Workflows.CUSTOM_PACKAGE_FLOW);

  const handleOnSave = useCallback(() => {
    onSave(formValues, setErrorMessage, setIsSaving);
  }, [formValues, onSave, setErrorMessage, setIsSaving]);

  const handleNameChange = useCallback(
    (name: string) => {
      setFormValues({ ...formValues, name });
      if (errorMessage) {
        setErrorMessage(null);
      }
    },
    [formValues, setFormValues, errorMessage, setErrorMessage],
  );

  const onCancel = useCallback(() => {
    onClose();
    trackCustomPackageFlow(WorkflowSteps.CANCEL);
  }, [onClose, trackCustomPackageFlow]);

  return (
    <SimpleModal
      actionMessage={messages.save}
      cancelMessage={messages.cancel}
      headerMessage={messages[`header-${action}`]}
      isActionDisabled={isSaving || !formValues.name}
      onAction={handleOnSave}
      onCancel={onCancel}
      onClose={onClose}
      size='small'
    >
      {errorMessage && (
        <BannerMessageV2
          className={style.errorBanner}
          content={<Text textType={TextType.Paragraph} value={errorMessage} />}
          status={BannerStatus.Error}
        />
      )}
      <Text className={style.description} textType={TextType.Description} value={messages.description} />
      <div className={style.name}>
        <Text textType={TextType.Label} value={formMessages['form-label-name']} />
        <Input hasError={!!errorMessage} onChange={handleNameChange} type='text' value={formValues.name} />
      </div>
      <div>
        <Text textType={TextType.Label} value={formMessages['form-label-dimensions']} />
        <DimensionInput
          dimensions={formValues.dimensions}
          onChange={(dimensions) => setFormValues({ ...formValues, dimensions })}
          unit={unit}
        />
      </div>
      {shouldShowMaxWeight && (
        <FlexColumn gap={Gap.Space16}>
          {isWeightsEnabled && (
            <FlexColumn gap={Gap.Space4}>
              <Text textType={TextType.Label} value={formMessages['form-label-tare-weight']} />
              <Text className={style.helperText} textType={TextType.Caption} value={formMessages['form-helper-tare-weight']} />
              <WeightInput
                onChange={(tareWeight) => setFormValues({ ...formValues, tareWeight })}
                unit={weightInputUnit}
                weight={formValues.tareWeight}
              />
            </FlexColumn>
          )}
          <FlexColumn gap={Gap.Space4}>
            <Text textType={TextType.Label} value={formMessages['form-label-max-weight']} />
            <Text className={style.helperText} textType={TextType.Caption} value={formMessages['form-helper-max-weight']} />
            <WeightInput
              onChange={(maxWeight) => setFormValues({ ...formValues, maxWeight })}
              unit={weightInputUnit}
              weight={formValues.maxWeight}
            />
          </FlexColumn>
        </FlexColumn>
      )}
    </SimpleModal>
  );
};
